/**
 * @file: useUserLocalSettings.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  storageKeyWrapped,
  useAsyncStorage,
} from '@euler/functions/useAsyncStorage';
import { makeDebug } from '@euler/utils';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect } from 'react';

type UserLocalSettings = {
  noAlertBeforeNavigateAwayConstructionJob?: boolean;
  noAlertBeforeNavigateAwayDeliveryCheck?: boolean;
  rememberLastServiceSubjectAssignment?: boolean;
  noAlertBeforeNavigateAwayQuotationForm?: boolean;
  /**
   * When true, allow `useAutoPlayRemarkVoice` to override the iOS silent
   * switch via `enableAudioPlaybackInSilentMode`. Defaults to false (respect
   * the silent switch).
   */
  playOrderRemarkVoiceInSilentMode?: boolean;
  /**
   * When true, suppress automatic playback of the service remark voice on
   * entering the task detail page. Defaults to false (auto-play enabled).
   */
  disableAutoPlayOrderRemarkVoice?: boolean;
  /**
   * When true, the dashboard and history lists render the legacy task card
   * instead of the redesigned TaskCardV2. Defaults to false (use v2).
   */
  preferLegacyTaskCard?: boolean;
};

const debug = makeDebug('user-local-settings');

export function useUserLocalSettings() {
  const [settings, setSettings] = useAsyncStorage<UserLocalSettings>(
    'user.local_settings',
    {},
  );
  const updateSettings = useCallback(
    (
      values:
        | Partial<UserLocalSettings>
        | ((
            current: UserLocalSettings | null | undefined,
          ) => UserLocalSettings | null | undefined),
    ) => {
      if (typeof values === 'function') {
        setSettings(values);
      } else {
        setSettings(v => ({ ...v, ...values }));
      }
    },
    [setSettings],
  );
  useEffect(() => {
    // migrate legacy settings
    const migrateBooleanValue = (
      key: string,
      callback: (value: boolean) => void,
    ): void => {
      const wrappedKey = storageKeyWrapped(key);
      AsyncStorage.getItem(wrappedKey)
        .then(value => {
          if (value != null) {
            debug(`got legacy user local setting of ${key}: ${value}`);
            callback(JSON.parse(value));
            void AsyncStorage.removeItem(wrappedKey);
          }
          return undefined;
        })
        .catch(() => null);
    };

    migrateBooleanValue('construction.show_alert_before_nav_away', value => {
      updateSettings({ noAlertBeforeNavigateAwayConstructionJob: !value });
    });

    migrateBooleanValue('delivery_check.show_alert_before_nav_away', value => {
      updateSettings({ noAlertBeforeNavigateAwayDeliveryCheck: !value });
    });
  }, [updateSettings]);

  debug('user settings: %O', settings);

  return [settings as UserLocalSettings, updateSettings] as const;
}
